import { type DialogPassThroughOptions } from 'primereact/dialog';
import type { ReactNode } from 'react';
import { DialogSize } from './constraints';

export type SubmitHandler = (
    onSubmit: (config?: DialogConfig | null) => Promise<void>,
) => void;

export interface ConfirmCloseConfig {
    title: string;
    dialogName: string;
    confirmLabel?: string;
    cancelLabel?: string;
    isDirty: () => boolean;
}

export interface DialogContextValues<
    TState extends Record<string, unknown> = Record<string, unknown>,
> {
    open: (config: Omit<DialogConfig, 'type'>) => void;
    confirm: (config: Omit<DialogConfig, 'type'>) => void;
    form: (config: Omit<DialogConfig, 'type'>) => void;
    modal: (config: Omit<DialogConfig, 'type'>) => void;
    stepper: <TWrapperProps extends object = object>(
        config: Omit<StepperDialogConfig<TWrapperProps>, 'type'>,
    ) => void;
    close: () => void;
    loading: boolean;
    handleSubmit: SubmitHandler;
    disabled: boolean;
    setDisabled: (disabled: boolean) => void;
    onSubmit?: () => void;
    /** Custom state shared between dialog content and buttons */
    dialogState: TState;
    /** Update custom dialog state - triggers re-render of all useDialog consumers */
    setDialogState: (state: TState | ((prev: TState) => TState)) => void;
}

export interface ButtonConfig {
    label?: string;
    callback?: () => void;
    overrideDefault?: boolean;
}

export interface DialogButtonsConfig {
    submitLabel?: string;
    submitDisabled?: boolean | (() => boolean);
    submitIcon?: ReactNode;
    submitIconPos?: 'left' | 'right';
    content?: ReactNode;
    closeButton?: ButtonConfig;
}

export enum DialogType {
    Confirm = 'confirm',
    Form = 'form',
    Default = 'default',
    Modal = 'modal',
    Stepper = 'stepper',
}

export interface StepConfig {
    title: string;
    content: ReactNode;
}

export interface StepperDialogWrapper<TWrapperProps extends object = object> {
    component: React.ComponentType<TWrapperProps & { children: ReactNode }>;
    props: TWrapperProps;
}

export interface StepperDialogConfig<TWrapperProps extends object = object>
    extends DialogConfig {
    steps: StepConfig[];
    wrapper?: StepperDialogWrapper<TWrapperProps>;
    allStepsReached?: boolean;
    linear?: boolean;
}

export interface DialogConfig {
    type?: DialogType;
    content?: ReactNode;
    title?: ReactNode;
    subtitle?: ReactNode;
    size?: DialogSize;
    buttons?: DialogButtonsConfig;
    dialogClassName?: string;
    pt?: DialogPassThroughOptions;
    confirmClose?: ConfirmCloseConfig;
}
